import React, { memo, useState, PropsWithChildren, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import _uniqueId from "lodash/uniqueId";
import { IFileObj, EUploadStatus } from "../models/IFile";
import { useFileUploadService } from "../context/FileUploadServiceContext";

export interface IFileUploadElementProps {
    isDragActive: boolean;
    isLoading: boolean;
    isLoaded: boolean;
    name?: string
}

interface IFileUploadProps {
    accept: "image/*" | undefined;
    multiple?: boolean;
    files?: { id: string; name: string }[];
    onUploaded: (files: { id: string; name: string }[]) => void;
    onRemoved: (guid: string) => void;
    wrapElement: React.ComponentType<PropsWithChildren<IFileUploadElementProps>>;
    loaderElement: React.ComponentType<{
        file: IFileObj;
        onRemove: (file: IFileObj) => void;
    }>;
    listWrapperElement: React.ComponentType;
    name?: string
    disabled?: boolean


}

const noop = () => {
    console.log('noop');
    return undefined
};
let requests: any[];

const FileUploadBtn: React.FC<IFileUploadProps> = (props) => {

    const {
        accept,
        multiple = false,
        onUploaded = noop,
        onRemoved = noop,
        wrapElement: Elem,
        loaderElement: LoaderElement,
        listWrapperElement: ListWrapperElement,
        files: uploadedFiles,
        disabled = false
    } = props;

    const uploadService = useFileUploadService();
    if (!uploadService) {
        throw new Error("FileUploadService is not provided. Cannot use a file upload component.");
    }

    const [fileObjs, setFiles] = useState<IFileObj[]>([])
    const [isLoading, setLoading] = useState(false)
    const [isLoaded, setLoaded] = useState(false)


    const createUploadableFile = (file: File): IFileObj => ({
        id: _uniqueId(),
        name: file.name,
        file: file,
        status: EUploadStatus.Uploading
    })

    const createLoadedFile = (file: any): IFileObj => ({
        id: _uniqueId(),
        guid: file.id,
        status: EUploadStatus.Uploading
    })

    const updateFileObj = (id: string, newProps: any) => {
        setFiles((prevFiles) => prevFiles.map((fileObj) => fileObj.id === id ? { ...fileObj, ...newProps } : fileObj))
    }

    const removeFileObj = (id: string) => {
        setFiles((prevFiles) => prevFiles.filter((fileObj) => fileObj.id !== id))
    }


    const handleFiles = (acceptedFiles: File[] = []) => {
        !isLoaded && setLoaded(true)
        let newFiles: IFileObj[] = acceptedFiles.map((file: any) => createUploadableFile(file))
        setFiles((prevFiles) => multiple ? [...prevFiles, ...newFiles] : newFiles);
        setLoading(true)
        const requests = newFiles.map((fileObj) => handleSingleUpload(fileObj));
        Promise.all(requests).then(function (values) {
            const succesfull = values.filter((file: any) => file.status === EUploadStatus.Done)
            const uploaded = succesfull.map(({ guid, name }: any) => ({ id: guid, name }))
            onUploaded(uploaded)
            setLoading(false);
        })
    }

    const handleSingleUpload = (newFile: IFileObj) => (
        new Promise(async (resolve, reject) => {
            try {
                if (!newFile.file) {
                    throw Error('Missing file')
                }
                const uploadResult = await uploadService.uploadFileWithUpload5(newFile.file);
                if (uploadResult.isSuccess) {
                    const newProps = {
                        guid: uploadResult.value.id,
                        status: EUploadStatus.Done
                    }
                    updateFileObj(newFile.id, newProps)
                    return resolve({ ...newFile, ...newProps })
                } else {
                    throw Error('Failed request')
                }
            } catch (err) {
                console.log("err", err)

                updateFileObj(newFile.id, { status: EUploadStatus.UploadError })
                return resolve(newFile)
            }
        })
    )

    const handleSingleFileInfo = (newFile: IFileObj) => (
        new Promise(async (resolve: any, reject: any) => {
            try {
                if (!newFile.guid) {
                    throw Error('Missing file')
                }
                const uploadResult = await uploadService.getImageInfo(newFile.guid, 400);
                if (uploadResult.isSuccess) {
                    updateFileObj(newFile.id, {
                        status: EUploadStatus.Done,
                        name: uploadResult.value.nimi,
                        preview: uploadResult.value.naytekuva ? uploadResult.value.naytekuva.base64Data : undefined
                    })
                    return resolve(newFile)
                } else {
                    throw Error('Failed request')
                }
            } catch (err) {
                updateFileObj(newFile.id, { status: EUploadStatus.UploadError })
                return resolve(newFile)
            }

        })
    )

    const handleSingleRemove = (newFile: IFileObj) => {
        if (!newFile.guid) {
            console.log('Missing file')
            return
        }
        removeFileObj(newFile.id)
        onRemoved(newFile.guid)
    }


    // Load initial files.
    useEffect(() => {
        let timeout = setTimeout(() => {
            if (uploadedFiles && uploadedFiles.length && !isLoaded) {
                setLoaded(true)
                let newFiles: IFileObj[] = uploadedFiles.map((file: any) => createLoadedFile(file))
                setFiles(newFiles)
                requests = newFiles.map((fileObj) => handleSingleFileInfo(fileObj));
                Promise.all(requests).then(function (values) { });
                //   requests.forEach((req: any) => req.cancel())
            }
        }, 50);
        return () => {
            clearTimeout(timeout)
        }

    }, [uploadedFiles, fileObjs])  // eslint-disable-line react-hooks/exhaustive-deps


    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDropAccepted: handleFiles,
        accept,
        disabled
    });

    return (
        <div className="FileUpload_wrapper">
            <div {...getRootProps()}>
                <Elem
                    isDragActive={isDragActive}
                    isLoading={isLoading}
                    isLoaded={isLoaded}
                    name={props.name}
                >
                    <input name={props.name} {...getInputProps({ multiple })} />
                </Elem>
            </div>
            <ListWrapperElement>
                {fileObjs.map((file, index) => (
                    <LoaderElement
                        key={file.id}
                        file={file}
                        onRemove={handleSingleRemove}
                    />

                ))}
            </ListWrapperElement>
        </div>
    );
};

export default memo(FileUploadBtn);
